import { Component } from '@angular/core';
import { ChartDataset } from 'chart.js';
import { GRAPH_PANEL_IMPORTS, GraphPanelComponent } from 'src/app/panels/components/graph-panel/graph-panel.component';
import { PanelComponent } from 'src/app/panels/components/panel.component';
import { DisplayUnit } from 'src/app/panels/models/display-unit';
import { GenerateGraphDataset, GraphType, IGraphConfig, IGraphMappedDataset } from 'src/app/panels/models/graph';
import { PanelType } from 'src/app/panels/models/panel-type';
import { Color } from 'src/app/shared/services/color/colors';

@PanelComponent.register
@Component({
  templateUrl: '../../../../panels/components/graph-panel/graph-panel.component.html',
  styleUrl: '../../../../panels/components/graph-panel/graph-panel.component.scss',
  selector: 'app-degree-days-graph-panel',
  imports: GRAPH_PANEL_IMPORTS,
})
export class DegreeDaysGraphPanelComponent extends GraphPanelComponent {
  public static override readonly type = PanelType.DegreeDaysGraph;
  protected override unit = DisplayUnit.Kwh;

  protected override graphInfo = {
    title: 'Graaddagen grafiek',
    description: `
        <h2>Wat laat deze grafiek zien?</h2>
        <p>
          Deze grafiek laat zien hoeveel energie een gebouw gebruikt in relatie tot de buitentemperatuur.
          Hierbij wordt onderscheid gemaakt tussen verwarmingsbehoefte en koelbehoefte.
        </p>

        <ul>
          <li>
            <strong>Graaddagen</strong> geven inzicht in koude perioden waarin een gebouw verwarmd moet worden.
          </li>
          <li>
            <strong>Koelgraaddagen</strong> geven inzicht in warme perioden waarin een gebouw actief gekoeld moet worden.
          </li>
        </ul>

        <p>
          Door het energieverbruik te relateren aan graaddagen en koelgraaddagen ontstaat een eerlijker beeld van de
          klimaatprestatie van een gebouw, onafhankelijk van weersinvloeden.
        </p>

        <h2>Wat is het verschil?</h2>
        <p>
          Bij graaddagen wordt gekeken hoeveel graden de buitentemperatuur onder de referentietemperatuur ligt.
          Dit zegt iets over de verwarmingsvraag van het gebouw.
        </p>

        <p>
          Bij koelgraaddagen wordt gekeken hoeveel graden de buitentemperatuur boven de referentietemperatuur ligt.
          Dit zegt iets over de koelvraag van het gebouw.
        </p>

        <p>
          Zo kunnen zowel winter- als zomerprestaties van een gebouw inzichtelijk gemaakt worden.
        </p>

        <h2>Hoe kun je deze grafiek gebruiken?</h2>
        <p>
          Met deze grafiek kun je beoordelen hoe efficiënt een gebouw omgaat met verwarmen en koelen.
          Lagere waarden betekenen dat minder energie nodig is om het gebouw comfortabel te houden onder
          vergelijkbare weersomstandigheden.
        </p>
        <p>
          <strong>Tip:</strong><br>
          Wil je zien of aanpassingen aan je pand effect hebben gehad, zoals betere isolatie of nieuwe installaties?<br>
          Zet in de datepicker de optie <strong>“Vergelijken”</strong> aan. Je kunt dan eenvoudig twee perioden naast elkaar bekijken en direct zien of je pand zuiniger is geworden.
        </p>
      `,
  };

  protected override config: IGraphConfig = {
    sortOrder: [
      GraphType.EnvironmentAmountHeatingDegreeDays,
      GraphType.EnvironmentAmountCoolingDegreeDays,
      GraphType.EnvironmentElectricityPerHeatingDegreeDay,
      GraphType.EnvironmentElectricityPerCoolingDegreeDay,
      GraphType.EnvironmentGasPerHeatingDegreeDay,
      GraphType.EnvironmentGasPerCoolingDegreeDay,
    ],
    legendSortOrder: [
      GraphType.EnvironmentAmountHeatingDegreeDays,
      GraphType.EnvironmentAmountCoolingDegreeDays,
      GraphType.EnvironmentElectricityPerHeatingDegreeDay,
      GraphType.EnvironmentElectricityPerCoolingDegreeDay,
      GraphType.EnvironmentGasPerHeatingDegreeDay,
      GraphType.EnvironmentGasPerCoolingDegreeDay,
    ],
    getGraphTypeDataset: this.getGraphTypeDataset,
  };

  private getGraphTypeDataset(
    generateDataset: GenerateGraphDataset,
    dataset: IGraphMappedDataset,
    startTimestamps: string[],
    endTimestamps: string[],
  ): ChartDataset {
    switch (dataset.type) {
      case GraphType.EnvironmentElectricityPerHeatingDegreeDay:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          'Electriciteit per graaddag',
          Color.Red,
          DisplayUnit.KwhDegreeDay,
          'bar',
        );
      case GraphType.EnvironmentElectricityPerCoolingDegreeDay:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          'Electriciteit per koelgraaddag',
          Color.Blue,
          DisplayUnit.KwhDegreeDay,
          'bar',
        );
      case GraphType.EnvironmentGasPerHeatingDegreeDay:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          'Gas per graaddag',
          Color.Red,
          DisplayUnit.KwhDegreeDay,
          'bar',
        );
      case GraphType.EnvironmentGasPerCoolingDegreeDay:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          'Gas per koelgraaddag',
          Color.Blue,
          DisplayUnit.KwhDegreeDay,
          'bar',
        );
      case GraphType.EnvironmentAmountHeatingDegreeDays:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          'Graaddagen',
          Color.Orange,
          DisplayUnit.DegreeDay,
          'line',
        );
      case GraphType.EnvironmentAmountCoolingDegreeDays:
        return generateDataset(
          dataset,
          startTimestamps,
          endTimestamps,
          'Koel graaddagen',
          Color.LightBlue,
          DisplayUnit.DegreeDay,
          'line',
        );
      default:
        throw new Error(`No dataset specified for GraphType '${dataset.type}'`);
    }
  }
}
